import { motion } from "framer-motion";
import { Headphones, ArrowRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Section } from "./shared";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import type { SolutionData } from "@/data/solutionsData";

interface Props {
  solution: SolutionData;
}

const SolutionFeaturedPodcast = ({ solution }: Props) => {
  const lang = useLanguage();
  const pre = langPrefix(lang);
  const podcast = solution.featuredPodcast;
  if (!podcast) return null;

  return (
    <Section>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          whileHover={{ y: -4, boxShadow: "0 24px 48px -12px hsla(212, 96%, 58%, 0.14)" }}
          className="group rounded-2xl border border-border/60 bg-card overflow-hidden flex flex-col md:flex-row transition-colors duration-300 hover:border-primary/20"
        >
          {podcast.image && (
            <Link to={`${pre}/podcasts/${podcast.slug}`} className="md:w-2/5 shrink-0 overflow-hidden relative block">
              <motion.img
                src={podcast.image}
                alt={podcast.title}
                className="w-full h-full object-cover aspect-[4/3] md:aspect-auto transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              {/* Play icon overlay */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center bg-primary/0 group-hover:bg-primary/15 transition-colors duration-300"
                initial={false}
              >
                <motion.div
                  className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-xl"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  whileHover={{ scale: 1.1 }}
                >
                  <Headphones size={32} strokeWidth={1.6} className="text-primary-foreground" />
                </motion.div>
              </motion.div>
            </Link>
          )}
          <div className="p-6 sm:p-8 flex flex-col justify-center gap-4">
            <motion.div
              className="flex items-center gap-2 text-primary"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Headphones size={18} strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-wider">Podcast</span>
            </motion.div>
            <motion.h3
              className="text-lg sm:text-xl font-bold text-foreground leading-snug"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              {podcast.title}
            </motion.h3>
            <motion.p
              className="text-sm text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              {podcast.subtitle}
            </motion.p>
            {podcast.speakers.length > 0 && (
              <motion.p
                className="text-xs text-muted-foreground"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.45 }}
              >
                Featuring: <span className="font-medium text-foreground">{podcast.speakers.join(" & ")}</span>
              </motion.p>
            )}
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <Link
                to={`${pre}/podcasts/${podcast.slug}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all mt-1"
              >
                Listen now <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default SolutionFeaturedPodcast;
